/* ============================================================================
   Tour do professor — COMO UM PASSO ENCONTRA O SEU ALVO
   ============================================================================
   Duas coisas aqui já foram diferentes e mudaram porque a realidade contradisse:

   1. ITEM EM DROPDOWN FECHADO ERA PULADO. Errado. O Sidebar não passa `show` ao
      `CSidebarNavDropdown`, e o `route` que faria o CoreUI abrir sozinho também
      não é passado (o Sidebar manda `to`, que cai como atributo no <li>) — então
      TODO dropdown do AVA nasce fechado. Seções com muitos filhos vivem
      exatamente aí — "Avaliações", que abriga a ADR, é o caso do tour. Pular era
      perder justamente os passos pedidos. Agora o tour ABRE o dropdown, clicando
      no toggle como o professor faria.

   2. O ALVO ERA SÓ O SELETOR DE HREF. Frágil: o menu vem do backend e o caminho
      pode não ser o que supomos. Agora o passo pode declarar `alvoPorTexto`, e a
      busca cai para o NOME visível do item quando o href não casa. Isso encontra
      inclusive o próprio cabeçalho da seção (o toggle do dropdown), que muitas
      vezes é o alvo mais honesto — é ele que diz "Avaliações".

   `alvoUsavel` continua sendo a régua de visibilidade, e continua ESTRUTURAL
   (classe `c-show`) e não geométrica, porque o jsdom não faz layout: uma
   checagem por `offsetParent`/`getClientRects` reprovaria todo alvo nos testes.
   ========================================================================== */

const DROPDOWN = ".c-sidebar-nav-dropdown";
const DROPDOWN_FECHADO = `${DROPDOWN}:not(.c-show)`;
const TOGGLE = ".c-sidebar-nav-dropdown-toggle";

const SIDEBAR = ".c-sidebar";
/* O CoreUI põe `c-sidebar-show` no <div class="c-sidebar"> quando ela está
   exibida (`isOpen === true && 'c-sidebar-show'` no CSidebar). Sem essa classe a
   sidebar está fora da tela — no celular isso é o estado normal, com o menu
   recolhido atrás do ☰. */
const SIDEBAR_OCULTA = `${SIDEBAR}:not(.c-sidebar-show)`;

/** Links e cabeçalhos de seção — os dois podem ser alvo de um passo de menu. */
const ITENS_DE_MENU = `.c-sidebar-nav-link, ${TOGGLE}`;

const normalizar = texto =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/**
 * O alvo está visível para a pessoa?
 *
 * Duas formas de estar no DOM sem estar na tela, e as duas são REVELÁVEIS pelo
 * orquestrador — daí não bastar "existe":
 *
 * - dentro de dropdown fechado (todo dropdown do AVA nasce fechado);
 * - dentro de sidebar recolhida, que no celular é o estado normal.
 */
export function alvoUsavel(elemento) {
  if (!elemento) return false;

  // `closest` não existe em nó de documento/fragmento; o alvo "body" cai aqui.
  if (typeof elemento.closest !== "function") return true;

  return (
    !elemento.closest(DROPDOWN_FECHADO) && !elemento.closest(SIDEBAR_OCULTA)
  );
}

/**
 * O alvo está dentro de uma sidebar recolhida?
 *
 * Existe separado de `alvoUsavel` porque revelar as duas coisas é diferente:
 * dropdown se abre com clique no toggle, sidebar depende de estado no Redux
 * (`setMenu`), que só o orquestrador tem como despachar.
 */
export function estaEmSidebarOculta(elemento) {
  if (!elemento || typeof elemento.closest !== "function") return false;

  return Boolean(elemento.closest(SIDEBAR_OCULTA));
}

/** O alvo mora na sidebar? (item de menu, cabeçalho de seção…) */
export function estaNaSidebar(elemento) {
  if (!elemento || typeof elemento.closest !== "function") return false;

  return Boolean(elemento.closest(SIDEBAR));
}

/**
 * A sidebar está exibida agora?
 *
 * Olha o DOM em vez do Redux de propósito: `general.showMenu` começa `true`, tem um
 * terceiro valor (`"responsive"`) e é persistido — não é retrato de visibilidade.
 * Quem manda é a classe que o CoreUI aplica.
 */
export function sidebarExibida(doc = document) {
  return Boolean(doc.querySelector(`${SIDEBAR}.c-sidebar-show`));
}

/**
 * Dropdowns fechados entre o elemento e a raiz, do MAIS EXTERNO para o mais
 * interno.
 *
 * A ordem importa: o toggle de um dropdown interno só existe de forma clicável
 * depois que o externo abriu. Abrir de dentro para fora erra o caso aninhado.
 */
export function dropdownsFechados(elemento) {
  const cadeia = [];
  let atual = elemento;

  while (atual && typeof atual.closest === "function") {
    const fechado = atual.closest(DROPDOWN_FECHADO);
    if (!fechado) break;

    cadeia.unshift(fechado);
    atual = fechado.parentElement;
  }

  return cadeia;
}

/**
 * Abre os dropdowns necessários para o alvo aparecer, clicando nos toggles.
 *
 * Clicar é de propósito: `isOpen` é estado interno do componente do CoreUI, e
 * mexer na classe `c-show` por fora deixaria o React e o DOM discordando — o
 * primeiro re-render desfaria. O clique passa pelo `toggle()` do próprio
 * componente, que ainda avisa o contexto da sidebar (é o que fecha os irmãos).
 *
 * @returns {number} quantos dropdowns foram abertos
 */
export function abrirDropdowns(elemento) {
  const cadeia = dropdownsFechados(elemento);

  cadeia.forEach(dropdown => {
    const toggle = dropdown.querySelector(TOGGLE);
    if (toggle) toggle.click();
  });

  return cadeia.length;
}

/**
 * Primeiro elemento que casa com o seletor, preferindo o que já está visível.
 *
 * Percorre TODOS os casamentos porque um seletor pode casar com um item
 * escondido e outro visível — é o caso das listas por vírgula, usadas quando o
 * backend pode devolver o caminho com outra caixa (`/userlist` vs `/Userlist`).
 *
 * Devolve o escondido quando é o único: ele é REVELÁVEL por `abrirDropdowns`, e
 * descartá-lo aqui era o motivo de a Avaliações ADR nunca aparecer.
 */
export function encontrarAlvo(seletor, doc = document) {
  if (!seletor) return null;

  const candidatos = [...doc.querySelectorAll(seletor)];

  return candidatos.find(alvoUsavel) || candidatos[0] || null;
}

/**
 * Item de menu cujo NOME visível contém um dos textos.
 *
 * Rede de segurança para quando o href do backend não é o que supomos. Casa
 * também com o cabeçalho da seção (toggle do dropdown), que costuma ser o alvo
 * mais fiel ao que o professor lê na tela.
 *
 * @param {string[]} textos  comparados sem acento e sem caixa
 */
export function encontrarPorTexto(textos, doc = document) {
  if (!Array.isArray(textos) || textos.length === 0) return null;

  const procurados = textos.map(criarPadrao).filter(Boolean);
  if (procurados.length === 0) return null;

  const candidatos = [...doc.querySelectorAll(ITENS_DE_MENU)].filter(item => {
    const nome = normalizar(item.textContent);
    return procurados.some(padrao => padrao.test(nome));
  });

  return candidatos.find(alvoUsavel) || candidatos[0] || null;
}

/**
 * Padrão que casa o texto no INÍCIO DE UMA PALAVRA do nome do item.
 *
 * Não é substring solta, e a diferença não é acadêmica: com `includes`, o token
 * "adr" casaria com "Quadro de horários". Também não é igualdade, porque o menu
 * escreve no plural e nós procuramos o radical — "turma" tem de achar "Minhas
 * turmas".
 */
function criarPadrao(texto) {
  const limpo = normalizar(texto);
  if (!limpo) return null;

  const escapado = limpo.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  return new RegExp(`\\b${escapado}`);
}

/**
 * O alvo de um passo: tenta o seletor e, se não achar, o nome visível.
 *
 * NÃO abre dropdown — só encontra. Quem revela é o orquestrador, depois de
 * decidir que vai mostrar aquele passo (ver `irPara` em index.jsx).
 */
export function resolverAlvo(passo, doc = document) {
  return (
    encontrarAlvo(passo.alvo, doc) || encontrarPorTexto(passo.alvoPorTexto, doc)
  );
}

/** `alvoExiste` no formato que `prepararPassos` espera. */
export const criarVerificador =
  (doc = document) =>
  passoOuSeletor =>
    Boolean(
      typeof passoOuSeletor === "string"
        ? encontrarAlvo(passoOuSeletor, doc)
        : resolverAlvo(passoOuSeletor, doc)
    );
